import type { TenantPublicConfig } from '@/types/TenantConfig'
import { AXES, isValidAxisValue } from '@/config/themes'
import { getPack, DEFAULT_PACK } from '@/design/packs'
import { useTenantStore } from '@/stores/tenant'
import { useUIStore } from '@/stores/ui'

declare global {
    interface Window { __TENANT__?: TenantPublicConfig }
}

/**
 * Pre-mount tenant boot (docs/tenant-config-model.md §5): resolve the tenant's public config
 * — window.__TENANT__ if the edge injected it (prod), otherwise GET /api/v1/public/config —
 * and apply it as the TENANT layer of the precedence chain (user ?? tenant ?? default):
 * appearance axes and locale apply only where the user has no stored override; title and
 * favicon always follow the tenant. Fails soft: on timeout/404 the app boots with defaults.
 */
export async function bootTenantConfig(): Promise<void> {
    const config = window.__TENANT__ ?? (await fetchPublicConfig())
    if (!config) return

    useTenantStore().setConfig(config)
    const uiStore = useUIStore()

    // Appearance = tenant'ın tasarım kimliği (yalnız TAP'tan yönetilir) — koşulsuz uygulanır.
    // Kaynak: çözülmüş WEB yüzey bloğu (sunucu kaskadı çözer); eski config'lerde köprü alana düşer.
    // Kullanıcı-override katmanı YOK (agency'deki görünüm paneli kaldırıldı); paket seçiliyse
    // eksenlerin tenant katmanı pakettir (kilitli).
    const webAppearance = config.surfaces?.web?.appearance ?? config.appearance
    const pack = getPack(webAppearance?.['pack'])
    const appearance = pack.id !== DEFAULT_PACK ? { ...pack.axes } : (webAppearance ?? {})
    document.documentElement.setAttribute('data-pack', pack.id)
    document.documentElement.setAttribute('data-dash', pack.dash)
    for (const axis of AXES) {
        const tenantValue = appearance[axis.key]
        if (tenantValue && isValidAxisValue(axis, tenantValue)) {
            uiStore.setAxis(axis.key, tenantValue)
        }
    }
    // Mode (dark/light) — tenant belirler
    const mode = appearance['mode']
    if (mode === 'dark' || mode === 'light') {
        uiStore.setTheme(mode)
    }
    // Locale default — user override wins
    const loc = config.locale?.defaultLocale
    if (loc && !localStorage.getItem('user-locale')) {
        await uiStore.setLocale(loc)
        localStorage.removeItem('user-locale')
    }
    // Identity — always tenant-driven
    if (config.identity?.appName) document.title = config.identity.appName
    if (config.identity?.favicon) {
        // index.html'deki TÜM varsayılan icon linkleri kalkmalı: tek birinin href'ini
        // değiştirmek yetmez, tarayıcı kalan png/ico girdilerini tercih edip tenant
        // favicon'unu hiç göstermeyebiliyor.
        document.querySelectorAll("link[rel~='icon']").forEach(l => l.remove())
        const link = document.createElement('link')
        link.rel = 'icon'
        if (config.identity.favicon.endsWith('.svg')) link.type = 'image/svg+xml'
        link.href = config.identity.favicon
        document.head.appendChild(link)
    }
    // Ana ekrana ekleme: iOS adı ve ikonu buradan alır (SVG ikon kabul etmez → PNG logoIcon/favicon).
    const homeName = config.identity?.brandName || config.identity?.appName
    if (homeName) setHeadTag('meta', 'name', 'apple-mobile-web-app-title', 'content', homeName)
    const touchIcon = [config.identity?.logoIcon, config.identity?.favicon].find(u => u && !u.endsWith('.svg'))
    if (touchIcon) setHeadTag('link', 'rel', 'apple-touch-icon', 'href', touchIcon)
    // iOS durum çubuğu temaya uyar: koyu temada siyah, açıkta varsayılan (açık).
    setHeadTag('meta', 'name', 'apple-mobile-web-app-status-bar-style', 'content', uiStore.theme === 'light' ? 'default' : 'black')
    // Android/Chrome: tarayıcı çubuğu rengi + yüklenebilir uygulama tanımı tenant'tan.
    const bg = getComputedStyle(document.body).getPropertyValue('--color-bg-primary').trim()
    if (bg) setHeadTag('meta', 'name', 'theme-color', 'content', bg)
    void applyTenantManifest(config, bg)
}

/**
 * Chrome yüklenebilirlik için manifest'te ad + ≥192 ve ≥512 px PNG ikon ister; ikisi de
 * tenant'a göre değiştiğinden manifest boot'ta üretilip blob URL olarak bağlanır. İkon
 * boyutları sabit yazılmaz, görselden okunur. Boot'u bekletmez.
 */
async function applyTenantManifest(config: TenantPublicConfig, bg: string) {
    const id = config.identity
    const name = id?.appName || id?.brandName
    if (!name) return
    const urls = [...new Set([id?.logoIcon, id?.favicon].filter((u): u is string => !!u && !u.endsWith('.svg')))]
    const icons = (await Promise.all(urls.map(iconEntry))).filter((i): i is ManifestIcon => !!i)
    const origin = window.location.origin
    const manifest = {
        name,
        short_name: id?.brandName || name,
        start_url: origin + '/',
        scope: origin + '/',
        display: 'standalone',
        ...(bg ? { background_color: bg, theme_color: bg } : {}),
        icons,
    }
    const href = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }))
    setHeadTag('link', 'rel', 'manifest', 'href', href)
}

interface ManifestIcon { src: string; sizes: string; type?: string; purpose: string }

const ICON_TYPES: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' }

function iconEntry(src: string): Promise<ManifestIcon | null> {
    return new Promise(resolve => {
        const img = new Image()
        img.onload = () => resolve(img.naturalWidth
            ? { src, sizes: `${img.naturalWidth}x${img.naturalHeight}`, type: ICON_TYPES[(src.split('?')[0] ?? '').split('.').pop()?.toLowerCase() ?? ''], purpose: 'any' }
            : null)
        img.onerror = () => resolve(null)
        img.src = src
    })
}

function setHeadTag(tag: 'meta' | 'link', keyAttr: string, key: string, valueAttr: string, value: string) {
    let el = document.head.querySelector(`${tag}[${keyAttr}="${key}"]`)
    if (!el) {
        el = document.createElement(tag)
        el.setAttribute(keyAttr, key)
        document.head.appendChild(el)
    }
    el.setAttribute(valueAttr, value)
}

async function fetchPublicConfig(): Promise<TenantPublicConfig | null> {
    try {
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), 2000)
        const res = await fetch('/api/v1/public/config', { signal: controller.signal })
        clearTimeout(timer)
        if (!res.ok) return null
        return (await res.json()) as TenantPublicConfig
    } catch {
        return null // fail soft — boot with defaults
    }
}
